import {
  GetWebDashboard200Response,
  TabVersion,
  WebDashboardTabImpactBlock,
  WebDashboardTabImport,
  WebDashboardTabsConfig,
} from "@coeff/api";
import { getImportIcon } from "@coeff/components";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CoeffSpinnerIcon,
  ExcelSheetsIcon,
  GoogleSheet,
  GoogleSheetsFileIcon,
  InfoCircleOutlineIcon,
  RefreshDotted,
} from "../../components";
import { isAxiosError, useApiContext } from "../../utils";

import { DisableTabModal } from "./DisableTabModal";
import { MobileBottomSheet } from "./MobileBottomSheet";
import { ToggleSwitch } from "./ToggleSwitch";
import { useIsMobile } from "./useIsMobile";
import { useUpdateWebDashboardTabsConfigMutation } from "./webDashboard";

const IMPORTS_REFRESH_NOTE = "Any Coefficient imports in the sheet refresh on their own schedules.";

// Status colors track the dashboard theme's semantic tokens, which applyTailwindTheme publishes
// as --coeff-negative/--coeff-positive on BOTH the iframe and the parent document (see
// applyTheme.ts). They are mode-aware — DEFAULT_SEMANTIC_TOKENS brightens red/green on dark
// themes — so pinning a light-mode hex here would leave this popover's errors unreadable on a
// dark dashboard while the metric-card deltas beside it adapt correctly.
//
// Set inline rather than as Tailwind classes: MobileBottomSheet falls back to the parent
// document when no portalDocument is passed, and the iframe-only style tag that serves the
// `.metric-status-*` classes isn't there. The literal fallbacks cover the first paint, before a
// theme resolves.
const NEGATIVE_COLOR = "var(--coeff-negative, #de1135)";

const POSITIVE_COLOR = "var(--coeff-positive, #0e8345)";

const POSITIVE_TINT_BG = "color-mix(in srgb, var(--coeff-positive, #0e8345) 12%, transparent)";

// Per-row hover tooltip copy, per the Figma annotation ("On hover show tooltip with last refresh
// time stamp for all import tabs"): “Opportunities” import last refreshed today at 9:00 AM.
// Lowercased day words because the phrase continues a sentence.
const LAST_REFRESHED_FORMATS = {
  sameDay: "[today at] h:mm A",
  lastDay: "[yesterday at] h:mm A",
  lastWeek: "[last] dddd [at] h:mm A",
  sameElse: "[on] MMM D, YYYY [at] h:mm A",
};

// Small "ⓘ" with a CSS hover tooltip (native title= has a ~1s browser delay before showing).
// `placement` chooses whether the bubble opens above ("top") or below ("bottom") the icon —
// use "bottom" near the top of the modal so it isn't clipped off the top edge.
const InfoTooltip: React.FC<{ content: React.ReactNode; placement?: "top" | "bottom" }> = ({
  content,
  placement = "top",
}) => (
  <span className="group relative inline-flex">
    <InfoCircleOutlineIcon
      width={16}
      height={16}
      className="cursor-help text-main-muted-foreground"
    />
    <span
      className={`pointer-events-none absolute left-1/2 z-50 hidden w-max max-w-[280px] -translate-x-1/2 rounded-md border border-main-border bg-main-background px-3 py-2 text-xs font-medium leading-4 text-main-foreground shadow-lg group-hover:block ${
        placement === "top" ? "bottom-full mb-2" : "top-full mt-2"
      }`}
    >
      {content}
      <span
        className={`absolute left-1/2 h-0 w-0 -translate-x-1/2 border-x-4 border-x-transparent ${
          placement === "top"
            ? "top-full border-t-4 border-t-main-border"
            : "bottom-full border-b-4 border-b-main-border"
        }`}
      />
    </span>
  </span>
);

const AutoIncludeNewTabsToggle: React.FC<{
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}> = ({ checked, disabled, onChange }) => (
  <div className="flex items-center pt-1">
    <label
      className={`flex items-center gap-2 ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
        className="accent-primary-button-background w-4 h-4"
      />
      <span className="text-sm">Auto include new tabs</span>
    </label>
  </div>
);

// "Saved! Refresh to see changes" pill shown at the top of the popover after an auto-save.
const PopoverToast: React.FC<{
  kind: "saved" | "error";
  message?: string;
  onClose: () => void;
}> = ({ kind, message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="absolute left-1/2 top-2 z-10 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-lg border border-main-border bg-main-background py-2 pl-3 pr-4 shadow-lg">
      {kind === "saved" ? (
        <>
          <span
            className="flex size-5 items-center justify-center rounded-full"
            style={{ background: POSITIVE_TINT_BG }}
          >
            <span className="material-icons !text-sm" style={{ color: POSITIVE_COLOR }}>
              check
            </span>
          </span>
          <span className="text-xs font-semibold text-main-foreground">Saved!</span>
          <span className="text-xs font-medium text-main-foreground">Refresh to see changes</span>
        </>
      ) : (
        <>
          <span className="material-icons !text-base" style={{ color: NEGATIVE_COLOR }}>
            error
          </span>
          <span className="text-xs font-semibold text-main-foreground">{message}</span>
        </>
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="ml-2 flex cursor-pointer items-center text-main-muted-foreground hover:text-main-foreground"
      >
        <span className="material-icons !text-sm">close</span>
      </button>
    </div>
  );
};

interface SourceSheetModalProps {
  dashboardData: GetWebDashboard200Response | undefined;
  isEditingEnabled: boolean;
  isRefreshing: boolean;
  /** Refresh pre-flight (diff / impact lookup) is in progress. */
  isCheckingChanges?: boolean;
  trialExpired?: boolean;
  /** Local Excel upload in progress (handled by parent modal). */
  isUploadingLocalExcel?: boolean;
  handleRefreshDashboard: (
    tabsConfig?: WebDashboardTabsConfig,
    excelUploadS3Key?: string,
    spreadsheetName?: string,
    excelUploadClientMetrics?: {
      file_size_bytes: number;
      upload_type: "post" | "multipart";
      upload_duration_ms: number;
      multipart_part_count?: number;
    }
  ) => void;
  handleOpenSheet: () => void;
  onClose: () => void;
  /** Web dashboard renders inside an iframe; modals must portal into that document. */
  portalDocument?: Document;
  /** Open the local Excel upload modal (rendered outside this dropdown). */
  onOpenLocalExcelUploadModal?: () => void;
  /** Deletes the given blocks from the dashboard ("Also delete affected charts"). */
  onDeleteBlocks?: (blockIds: string[]) => void;
  /** Attached to the sheet container so navbar click-outside detection works through the portal. */
  rootRef?: React.Ref<HTMLDivElement>;
}

const getEnabledTabIds = (config: WebDashboardTabsConfig | undefined): Set<number> => {
  return config?.include_tabs ? new Set(config.include_tabs.map(t => t.tab_id)) : new Set();
};

const getAutoIncludeNewTabs = (config: WebDashboardTabsConfig | undefined): boolean => {
  return !config?.exclude_new_tabs;
};

const saveErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    if (error.response?.status === 409) {
      return "A refresh is in progress — try again when it finishes.";
    }
    if (error.response?.status === 422) {
      return "At least one tab must stay enabled.";
    }
  }

  return "Couldn't save tab changes. Try again.";
};

export const SourceSheetModal: React.FC<SourceSheetModalProps> = ({
  dashboardData,
  isEditingEnabled,
  isRefreshing,
  isCheckingChanges = false,
  trialExpired = false,
  isUploadingLocalExcel = false,
  handleRefreshDashboard,
  handleOpenSheet,
  onClose,
  portalDocument,
  onOpenLocalExcelUploadModal,
  onDeleteBlocks,
  rootRef,
}) => {
  const { apiClient } = useApiContext();

  const dashboardId = dashboardData?.web_dashboard_id;

  const isExcel = (dashboardData as any)?.source_type === "excel";

  const excelUrl = (dashboardData as any)?.source_workbook_url as string | undefined;

  const excelFileIdentifier = (dashboardData as any)?.excel_file_identifier as
    | { drive_id?: string | null; not_accessible_from_backend?: boolean | null }
    | undefined;

  // Local Excel has no web link to open — hide the open-workbook affordance for those dashboards.
  const isLocalExcel =
    isExcel &&
    !excelUrl &&
    (!!excelFileIdentifier?.not_accessible_from_backend || !excelFileIdentifier?.drive_id);

  // Cloud Excel reads the workbook via Microsoft Graph, which serves the last *persisted*
  // copy. Recent in-app edits can take a couple of minutes to propagate, so a refresh
  // triggered right after editing may pull slightly stale data. Warn the user.
  const isCloudExcel = isExcel && !isLocalExcel;

  const serverConfig = dashboardData?.tabs_config;

  const importsSummary = dashboardData?.imports_summary;

  const updateTabsConfigMutation = useUpdateWebDashboardTabsConfigMutation(dashboardId);

  const isSaving = updateTabsConfigMutation.isLoading;

  const [isEditingTabs, setIsEditingTabs] = useState(false);

  const [enabledTabIds, setEnabledTabIds] = useState<Set<number>>(() =>
    getEnabledTabIds(serverConfig)
  );

  const [autoIncludeNewTabs, setAutoIncludeNewTabs] = useState(() =>
    getAutoIncludeNewTabs(serverConfig)
  );

  // `id` keys the toast element so a replacement toast restarts its dismiss countdown.
  const [toast, setToast] = useState<{
    id: number;
    kind: "saved" | "error";
    message?: string;
  } | null>(null);

  const toastIdRef = useRef(0);

  const showToast = useCallback((kind: "saved" | "error", message?: string) => {
    toastIdRef.current += 1;
    setToast({ id: toastIdRef.current, kind, message });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const [disableCandidate, setDisableCandidate] = useState<TabVersion | null>(null);

  // Mirrors disableCandidate's tab_id so in-flight tab_impact responses can tell whether
  // they still belong to the open modal (see handleToggleTab).
  const disableCandidateIdRef = useRef<number | null>(null);

  const [affectedBlocks, setAffectedBlocks] = useState<WebDashboardTabImpactBlock[]>([]);

  const [isImpactLoading, setIsImpactLoading] = useState(false);

  // Re-sync from the server config whenever it changes (the dashboard query can resolve, or
  // swap from the public payload to the authenticated one, while this popover is mounted;
  // every auto-save also round-trips through the query cache). Skipped while a save is in
  // flight so the optimistic toggle isn't clobbered by the pre-save cache value.
  //
  // NOTE: master guarded this with a `hasInitializedFromConfig` ref — re-init once, never
  // again, "so in-progress user toggles survive background refetches". Re-syncing on every
  // config change gives that up, and `!isSaving` doesn't replace it: it only covers the window
  // while the PUT is in flight, and the problem case is a GET that lands *after* the save
  // settles. A content save invalidates [WEB_DASHBOARD] (see useSaveDashboardConfigMutation),
  // so a large dashboard GET issued before the toggle can be delivered after it and reset
  // enabledTabIds to the pre-save value — the toggle visibly flips back just after "Saved!".
  //
  // Accepted as a corner case for ENG-7204: it needs that GET to be served before the PUT
  // commits but delivered after, and the server keeps the saved config either way, so a
  // reload corrects it. The fix, if it ever bites: track the last successfully-saved config in
  // a ref and skip re-sync until an incoming config matches it.
  useEffect(() => {
    if (serverConfig !== undefined && !isSaving) {
      setEnabledTabIds(getEnabledTabIds(serverConfig));
      setAutoIncludeNewTabs(getAutoIncludeNewTabs(serverConfig));
    }
  }, [serverConfig, isSaving]);

  // Show all known tabs (included + excluded) so users can toggle excluded tabs back on
  const allConfigTabs = useMemo(() => {
    if (!serverConfig) {
      return [];
    }
    const included = serverConfig.include_tabs ?? [];
    const excluded = serverConfig.exclude_tabs ?? [];

    return [...included, ...excluded];
  }, [serverConfig]);

  const enabledCount = enabledTabIds.size;

  const includedTabs = useMemo(
    () => allConfigTabs.filter(t => enabledTabIds.has(t.tab_id)),
    [allConfigTabs, enabledTabIds]
  );

  const excludedTabs = useMemo(
    () => allConfigTabs.filter(t => !enabledTabIds.has(t.tab_id)),
    [allConfigTabs, enabledTabIds]
  );

  const importsByTab = useMemo(() => {
    const map = new Map<number | string, WebDashboardTabImport[]>();
    for (const imp of importsSummary?.imports ?? []) {
      const key = imp.tab_id ?? imp.tab_name;
      if (key === undefined) {
        continue;
      }
      map.set(key, [...(map.get(key) ?? []), imp]);
    }

    return map;
  }, [importsSummary]);

  const importsForTab = useCallback(
    (tab: TabVersion): WebDashboardTabImport[] =>
      importsByTab.get(tab.tab_id) ?? (tab.tab_name ? importsByTab.get(tab.tab_name) ?? [] : []),
    [importsByTab]
  );

  // Only imports on enabled tabs matter — a deliberately excluded broken tab shouldn't raise
  // the banner. Imports with no tab attribution are kept since they can't be ruled out.
  const erroringImports = useMemo(() => {
    const enabledNames = new Set(includedTabs.map(t => t.tab_name));

    return (importsSummary?.imports ?? []).filter(imp => {
      if (imp.health !== "erroring") {
        return false;
      }
      if (imp.tab_id !== undefined && imp.tab_id !== null) {
        return enabledTabIds.has(imp.tab_id);
      }
      if (imp.tab_name) {
        return enabledNames.has(imp.tab_name);
      }

      return true;
    });
  }, [importsSummary, includedTabs, enabledTabIds]);

  const saveTabsConfig = useCallback(
    (nextEnabledIds: Set<number>, nextAutoInclude: boolean, onSaved?: () => void) => {
      const tabsConfig: WebDashboardTabsConfig = {
        exclude_new_tabs: !nextAutoInclude,
        include_tabs: allConfigTabs.filter(t => nextEnabledIds.has(t.tab_id)),
        exclude_tabs: allConfigTabs.filter(t => !nextEnabledIds.has(t.tab_id)),
      };
      updateTabsConfigMutation.mutate(tabsConfig, {
        onSuccess: () => {
          showToast("saved");
          onSaved?.();
        },
        onError: error => {
          // Roll the optimistic toggle back to the server truth.
          setEnabledTabIds(getEnabledTabIds(serverConfig));
          setAutoIncludeNewTabs(getAutoIncludeNewTabs(serverConfig));
          showToast("error", saveErrorMessage(error));
        },
      });
    },
    [allConfigTabs, serverConfig, showToast, updateTabsConfigMutation]
  );

  const isRefreshBusy = isRefreshing || isCheckingChanges;

  const canEditTabs = isEditingEnabled && !isRefreshBusy && !isSaving;

  const handleToggleTab = useCallback(
    (tab: TabVersion) => {
      if (!canEditTabs) {
        return;
      }
      if (!enabledTabIds.has(tab.tab_id)) {
        const next = new Set(enabledTabIds);
        next.add(tab.tab_id);
        setEnabledTabIds(next);
        saveTabsConfig(next, autoIncludeNewTabs);

        return;
      }
      if (enabledTabIds.size <= 1) {
        showToast("error", "At least one tab must stay enabled.");

        return;
      }
      // Disabling: look up dependent charts first, then confirm in the Disable Tab modal.
      setDisableCandidate(tab);
      disableCandidateIdRef.current = tab.tab_id;
      setAffectedBlocks([]);
      if (dashboardId) {
        setIsImpactLoading(true);
        // A late response for a cancelled/replaced candidate must not leak into the
        // current modal — its charts would be the ones "Also delete" removes.
        const isCurrent = () => disableCandidateIdRef.current === tab.tab_id;
        apiClient
          .getWebDashboardTabImpact({ web_dashboard_id: dashboardId, tab_id: tab.tab_id })
          .then(response => {
            if (isCurrent()) {
              setAffectedBlocks(response.data.affected_blocks ?? []);
            }
          })
          // Best-effort: an unavailable impact lookup means "no known impact", not a blocker.
          .catch(() => {
            if (isCurrent()) {
              setAffectedBlocks([]);
            }
          })
          .finally(() => {
            if (isCurrent()) {
              setIsImpactLoading(false);
            }
          });
      }
    },
    [
      apiClient,
      autoIncludeNewTabs,
      canEditTabs,
      dashboardId,
      enabledTabIds,
      saveTabsConfig,
      showToast,
    ]
  );

  const handleConfirmDisable = useCallback(
    (deleteAffectedCharts: boolean) => {
      if (!disableCandidate) {
        return;
      }
      const next = new Set(enabledTabIds);
      next.delete(disableCandidate.tab_id);
      setEnabledTabIds(next);
      // Delete charts only once the disable actually saved — a 409/422 rolls the toggle
      // back, and deleting up front would leave the tab enabled with its charts gone.
      const blockIdsToDelete =
        deleteAffectedCharts && affectedBlocks.length > 0
          ? affectedBlocks.map(b => b.block_id)
          : [];
      saveTabsConfig(next, autoIncludeNewTabs, () => {
        if (blockIdsToDelete.length > 0) {
          onDeleteBlocks?.(blockIdsToDelete);
        }
      });
      setDisableCandidate(null);
      disableCandidateIdRef.current = null;
      setAffectedBlocks([]);
    },
    [
      affectedBlocks,
      autoIncludeNewTabs,
      disableCandidate,
      enabledTabIds,
      onDeleteBlocks,
      saveTabsConfig,
    ]
  );

  const handleAutoIncludeChange = useCallback(
    (checked: boolean) => {
      if (!canEditTabs) {
        return;
      }
      setAutoIncludeNewTabs(checked);
      saveTabsConfig(enabledTabIds, checked);
    },
    [canEditTabs, enabledTabIds, saveTabsConfig]
  );

  const lastRefreshedDt = dashboardData?.last_refreshed_dt;

  const lastUploadedDt = (dashboardData as any)?.excel_last_uploaded_dt as string | undefined;

  // Error-banner hover detail. Editors get the import name; viewers a generic line.
  const errorTooltip = useMemo(() => {
    if (erroringImports.length === 0) {
      return null;
    }
    if (erroringImports.length > 1) {
      return `${erroringImports.length} imports failed. Open the sheet and the Coefficient extension for full details.`;
    }
    const failed = erroringImports[0];
    const label = failed.data_import_name ?? failed.tab_name;

    return label
      ? `The “${label}” import failed. Open the sheet to see what went wrong and try refreshing the import.`
      : "A Coefficient import in this sheet failed its last run. Open the sheet to see what went wrong.";
  }, [erroringImports]);

  // Web dashboard renders inside an iframe; track the real device width via that window.
  const isMobile = useIsMobile(portalDocument?.defaultView);

  // Mobile is a read-only summary: a close button is the only control. Tab editing and
  // refresh live on desktop (see the note), so this omits all of those. Matches the Figma.
  if (isMobile) {
    return (
      <MobileBottomSheet
        onClose={onClose}
        targetDocument={portalDocument}
        containerRef={rootRef}
        ariaLabel="Source Sheet"
        onClick={e => e.stopPropagation()}
        className="flex flex-col items-start bg-main-background text-main-foreground gap-3 px-4 pt-3 pb-5"
        desktopClassName="absolute top-full right-0 mt-2 border border-main-border rounded-lg shadow-lg z-50 w-[460px]"
      >
        {/* Header: title + close */}
        <div className="flex items-center gap-1.5 w-full">
          <span className="material-icons !text-xl text-main-foreground shrink-0">grid_view</span>
          <span className="flex-1 min-w-0 font-semibold text-sm leading-[18px] text-main-foreground">
            Source Sheet
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex items-center justify-center size-6 shrink-0 rounded-full text-main-muted-foreground hover:bg-main-foreground/10 cursor-pointer"
          >
            <span className="material-icons !text-xl">close</span>
          </button>
        </div>

        {/* Source sheet summary */}
        <div className="flex items-center gap-1.5 w-full">
          {/* Circular light background behind the sheet logo (Figma: 40px circle, #f5f5f5). */}
          <div className="flex items-center justify-center size-10 shrink-0 rounded-full bg-main-foreground/5">
            {isExcel ? (
              <ExcelSheetsIcon width={24} height={24} />
            ) : (
              <GoogleSheetsFileIcon width={18} height={24} />
            )}
          </div>
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span className="font-bold text-sm leading-[18px] text-main-foreground truncate">
              {dashboardData?.spreadsheet_name ?? "Source Sheet"}
            </span>
            {lastRefreshedDt && (
              <span className="text-sm leading-[18px] text-main-muted-foreground truncate">
                Refreshed {moment(lastRefreshedDt).fromNow()}
              </span>
            )}
            {!isLocalExcel && (
              <button
                type="button"
                onClick={handleOpenSheet}
                className="w-fit text-xs font-medium leading-[18px] text-primary-button-background hover:text-primary-button-hover-background cursor-pointer"
              >
                {isExcel ? "Open workbook" : "Open sheet"}
              </button>
            )}
          </div>
        </div>

        {/* Refresh isn't available on mobile — direct users to desktop. */}
        <div className="w-full flex items-center justify-center rounded-lg bg-main-foreground/5 p-2.5">
          <span className="text-xs font-medium leading-[18px] text-main-muted-foreground text-center">
            To get the latest data, refresh dashboard on desktop
          </span>
        </div>
      </MobileBottomSheet>
    );
  }

  const tabsCountLine = serverConfig && (
    <span className="text-sm font-normal leading-[18px] text-main-muted-foreground">
      {enabledCount} of {allConfigTabs.length} tabs enabled{" "}
      {isEditingEnabled && allConfigTabs.length > 0 && (
        // Label stays "edit tabs" in both states (per the Figma, which shows the link and the
        // expanded lists together), so aria-expanded is what conveys open/closed.
        <button
          type="button"
          onClick={() => setIsEditingTabs(v => !v)}
          aria-expanded={isEditingTabs}
          className="cursor-pointer font-medium text-primary-button-background hover:text-primary-button-hover-background"
        >
          edit tabs
        </button>
      )}
    </span>
  );

  const errorBanner = erroringImports.length > 0 && (
    <div className="group relative flex w-full items-center gap-2 rounded-lg bg-main-background p-3">
      <span className="material-icons !text-base shrink-0" style={{ color: NEGATIVE_COLOR }}>
        error
      </span>
      <span
        className="min-w-0 flex-1 truncate text-xs font-semibold leading-[18px]"
        style={{ color: NEGATIVE_COLOR }}
      >
        Last import run failed on {erroringImports.length} Coefficient{" "}
        {erroringImports.length === 1 ? "import" : "imports"}
      </span>
      {!isLocalExcel && (
        <button
          type="button"
          onClick={handleOpenSheet}
          className="shrink-0 cursor-pointer text-xs font-semibold text-primary-button-background hover:text-primary-button-hover-background"
        >
          Open sheet
        </button>
      )}
      {errorTooltip && (
        <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 hidden w-max max-w-[300px] -translate-x-1/2 rounded-md border border-main-border bg-main-background px-3 py-2 text-xs font-medium leading-4 text-main-foreground shadow-lg group-hover:block">
          {errorTooltip}
        </span>
      )}
    </div>
  );

  const renderTabRow = (tab: TabVersion, isEnabled: boolean) => {
    const tabImports = importsForTab(tab);

    const failedImport = tabImports.find(imp => imp.health === "erroring");

    const importType = tabImports[0]?.data_source_type;

    const tabName = tab.tab_name ?? `Tab ${tab.tab_id}`;

    // A tab carries at most one import, so the first is the one to describe. No timestamp
    // means no tooltip — the Figma has no copy for an import that has never run. Gated to
    // non-local-Excel at the render site, matching the per-row error indicator: the local
    // Excel row is a compact variant that shows neither a logo nor import state.
    const primaryImport = tabImports[0];

    const refreshedTooltip = primaryImport?.last_run_dt
      ? `“${primaryImport.data_import_name ?? tabName}” import last refreshed ${moment(
          primaryImport.last_run_dt
        ).calendar(undefined, LAST_REFRESHED_FORMATS)}`
      : null;

    return (
      <div
        key={tab.tab_id}
        className={`group relative flex w-full items-center rounded-lg bg-main-background ${
          isLocalExcel ? "h-8 gap-2 px-3" : "gap-3 px-3 py-2"
        }`}
      >
        {!isLocalExcel && (
          // Fixed-size box that also constrains the svg itself — some logo components
          // (e.g. Quickbooks) don't spread props, so width/height props get dropped.
          //
          // The neutral plate normalizes the logo assets: only 31 of 233 carry their own
          // circular backdrop, so the rest would render as a bare logo next to a plated one.
          // A plated logo fills its whole viewBox, so its own tint covers this entirely — the
          // Salesforce blue / HubSpot peach / Sheets green in the Figma still come through.
          <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-main-foreground/5 [&_svg]:h-8 [&_svg]:w-8">
            {importType ? (
              getImportIcon(importType)
            ) : isExcel ? (
              <ExcelSheetsIcon />
            ) : (
              <GoogleSheet />
            )}
          </span>
        )}
        <span
          className={`min-w-0 truncate text-sm leading-[18px] ${
            isLocalExcel
              ? `flex-1 font-medium ${
                  isEnabled ? "text-main-foreground" : "text-main-muted-foreground"
                }`
              : "font-normal text-main-foreground"
          }`}
        >
          {tabName}
        </span>
        {!isLocalExcel && failedImport && (
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className="material-icons !text-xs shrink-0" style={{ color: NEGATIVE_COLOR }}>
              error
            </span>
            <span
              className="truncate text-xs font-semibold leading-[14px]"
              style={{ color: NEGATIVE_COLOR }}
            >
              Last import run failed
            </span>
          </span>
        )}
        {!isLocalExcel && !failedImport && <span className="min-w-0 flex-1" />}
        <ToggleSwitch
          checked={isEnabled}
          disabled={!canEditTabs}
          onChange={() => handleToggleTab(tab)}
          ariaLabel={tabName}
          offTrackClassName="bg-main-foreground/20"
        />
        {!isLocalExcel && refreshedTooltip && (
          // Opens downward over the next row: the tab lists scroll (overflow-y-auto), which
          // clips anything drawn outside them, so the bubble has to stay inside the list box.
          <span className="pointer-events-none absolute left-12 top-full z-50 mt-1 hidden w-max max-w-[260px] rounded-md border border-main-border bg-main-background px-3 py-2 text-xs font-medium leading-4 text-main-foreground shadow-lg group-hover:block">
            {refreshedTooltip}
          </span>
        )}
      </div>
    );
  };

  const tabSections = isEditingTabs && serverConfig && (
    <>
      {isLocalExcel ? (
        <>
          <span className="w-full text-sm font-semibold leading-[18px] text-main-foreground">
            Tabs
          </span>
          <div className="flex max-h-60 w-full flex-col gap-2 overflow-y-auto">
            {allConfigTabs.map(tab => renderTabRow(tab, enabledTabIds.has(tab.tab_id)))}
          </div>
        </>
      ) : (
        <>
          <span className="w-full text-sm font-medium leading-[18px] text-main-muted-foreground">
            Tabs included in dashboard
          </span>
          <div className="flex max-h-56 w-full flex-col gap-2 overflow-y-auto">
            {includedTabs.map(tab => renderTabRow(tab, true))}
          </div>
          {excludedTabs.length > 0 && (
            <>
              <span className="w-full text-sm font-medium leading-[18px] text-main-muted-foreground">
                Tabs excluded from dashboard
              </span>
              <div className="flex max-h-40 w-full flex-col gap-2 overflow-y-auto">
                {excludedTabs.map(tab => renderTabRow(tab, false))}
              </div>
            </>
          )}
        </>
      )}
      {/* Kept from the previous design (deliberate product decision, the Figma omits it). */}
      {isEditingEnabled && (
        <AutoIncludeNewTabsToggle
          checked={autoIncludeNewTabs}
          disabled={!canEditTabs}
          onChange={handleAutoIncludeChange}
        />
      )}
    </>
  );

  return (
    <MobileBottomSheet
      onClose={onClose}
      targetDocument={portalDocument}
      containerRef={rootRef}
      ariaLabel={isExcel ? "Source File" : "Source Sheet"}
      onClick={e => e.stopPropagation()}
      className="flex flex-col items-start bg-main-background text-main-foreground p-5 gap-2"
      desktopClassName="absolute top-full right-0 mt-2 border border-main-border rounded-lg shadow-lg z-50 w-[520px]"
    >
      {toast && (
        <PopoverToast
          key={toast.id}
          kind={toast.kind}
          message={toast.message}
          onClose={dismissToast}
        />
      )}

      {/* Header */}
      <div className="flex w-full items-center justify-between gap-5">
        <span className="text-sm font-semibold leading-[18px] text-main-foreground">
          {isExcel ? "Source File" : "Source Sheet"}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-main-muted-foreground hover:bg-main-foreground/10"
        >
          <span className="material-icons !text-base">close</span>
        </button>
      </div>

      {/* Source card */}
      <div
        className={`flex w-full flex-col bg-main-foreground/5 ${
          isLocalExcel ? "gap-2.5 rounded-lg p-2.5" : "gap-4 rounded-xl p-3"
        }`}
      >
        {/* Source item */}
        <div className="flex items-center gap-1.5 min-w-0">
          {isExcel ? (
            <ExcelSheetsIcon
              width={isLocalExcel ? 40 : 48}
              height={isLocalExcel ? 40 : 48}
              className="shrink-0"
            />
          ) : (
            <span className="flex size-12 shrink-0 items-center justify-center">
              <GoogleSheetsFileIcon width={25} height={34} />
            </span>
          )}
          <div className="flex min-w-0 flex-col gap-1">
            <span className="flex items-center gap-1 min-w-0">
              <span className="truncate text-sm font-semibold leading-[18px] text-main-foreground">
                {dashboardData?.spreadsheet_name ?? (isExcel ? "Source File" : "Source Sheet")}
              </span>
              {!isLocalExcel && (
                <button
                  type="button"
                  onClick={handleOpenSheet}
                  title={isExcel ? "Open Workbook" : "Open Sheet"}
                  aria-label={isExcel ? "Open Workbook" : "Open Sheet"}
                  className="flex shrink-0 cursor-pointer items-center text-main-foreground hover:text-primary-button-background"
                >
                  <span className="material-icons !text-base">open_in_new</span>
                </button>
              )}
            </span>
            {isLocalExcel && (
              <span className="flex items-center gap-1 text-sm font-normal leading-[18px] text-main-muted-foreground">
                <span className="truncate">
                  {lastUploadedDt
                    ? `Uploaded ${moment(lastUploadedDt).fromNow()}`
                    : "Not uploaded yet"}
                </span>
                <InfoTooltip content={IMPORTS_REFRESH_NOTE} placement="bottom" />
              </span>
            )}
            {tabsCountLine}
          </div>
        </div>

        {errorBanner}

        {tabSections}
      </div>

      {/* Footer */}
      <div className="flex w-full items-center gap-5 pt-3">
        {isEditingEnabled &&
          (isLocalExcel ? (
            <button
              type="button"
              onClick={() => onOpenLocalExcelUploadModal?.()}
              disabled={isUploadingLocalExcel || isRefreshBusy || isSaving}
              className="ml-auto flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded bg-primary-button-background/10 px-3 text-sm font-semibold text-primary-button-background hover:bg-primary-button-background/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isUploadingLocalExcel || isRefreshBusy ? (
                <CoeffSpinnerIcon width={20} height={20} className="loading-spinner shrink-0" />
              ) : (
                <RefreshDotted className="h-6 w-6 shrink-0" />
              )}
              {isUploadingLocalExcel || isRefreshBusy ? "Refreshing..." : "Upload file to refresh"}
            </button>
          ) : (
            <span className="group relative inline-flex shrink-0">
              <button
                type="button"
                onClick={() => handleRefreshDashboard()}
                disabled={trialExpired || isRefreshBusy || isSaving}
                className="flex h-10 cursor-pointer items-center gap-2 rounded-lg bg-primary-button-background/10 px-3 text-sm font-semibold text-primary-button-background hover:bg-primary-button-background/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isRefreshBusy ? (
                  <CoeffSpinnerIcon width={20} height={20} className="loading-spinner shrink-0" />
                ) : (
                  <RefreshDotted className="h-6 w-6 shrink-0" />
                )}
                {isRefreshBusy ? "Refreshing from Sheet" : "Refresh Dashboard"}
              </button>
              {trialExpired && (
                <span className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 hidden w-max max-w-[260px] rounded-md border border-main-border bg-main-background px-3 py-2 text-xs font-medium leading-4 text-main-foreground shadow-lg group-hover:block">
                  Your trial has expired. Upgrade to refresh.
                </span>
              )}
            </span>
          ))}
        {!isLocalExcel &&
          (isRefreshBusy ? (
            <span className="min-w-0 text-xs font-medium leading-4 text-main-muted-foreground">
              {IMPORTS_REFRESH_NOTE}
            </span>
          ) : (
            lastRefreshedDt && (
              <span className="flex min-w-0 items-center gap-2 text-xs font-medium leading-[14px] text-main-muted-foreground">
                <span className="truncate">Refreshed {moment(lastRefreshedDt).fromNow()}</span>
                <InfoTooltip content={IMPORTS_REFRESH_NOTE} />
              </span>
            )
          ))}
      </div>

      {isCloudExcel && !isRefreshing && (
        // Graph API serves the last *persisted* copy, which can lag live edits by ~2 min.
        // Surface this prominently (not just on hover) so users don't refresh prematurely
        // and pull stale data right after editing.
        <div className="w-full text-xs leading-4 text-main-muted-foreground">
          <span className="font-semibold text-main-foreground">
            Made recent edits to your Excel file?{" "}
          </span>
          Microsoft&apos;s API can take up to ~2 min to sync changes. Give it a moment before
          refreshing.
        </div>
      )}

      {disableCandidate && (
        <DisableTabModal
          tabName={disableCandidate.tab_name ?? `Tab ${disableCandidate.tab_id}`}
          affectedBlocks={affectedBlocks}
          isLoadingImpact={isImpactLoading}
          onCancel={() => {
            setDisableCandidate(null);
            disableCandidateIdRef.current = null;
            setAffectedBlocks([]);
            setIsImpactLoading(false);
          }}
          onProceed={handleConfirmDisable}
          portalDocument={portalDocument}
        />
      )}
    </MobileBottomSheet>
  );
};
